/**
 * Completion Context Detector
 *
 * Centralized logic for detecting what kind of completion context we're in
 * based on cursor position and surrounding text.
 */

import type { IPosition } from 'monaco-editor';

import { hasDefinedParameterCount } from './command-metadata.js';
import { OPTIONAL_ARGUMENT_NAMES } from './syntax-definitions.js';
import type { MonacoModel } from './types.js';

/**
 * Matches an optional argument name followed by `(` and captures everything
 * until the end of the string (no closing paren).  Used to detect when the
 * cursor is inside an optional argument's parentheses.
 */
const OPTIONAL_ARG_CONTEXT_REGEX = new RegExp(`\\b(${OPTIONAL_ARGUMENT_NAMES.join('|')})\\(([^)]*)$`);

export type CompletionContext = {
    type: 'none' | 'command' | 'parameter' | 'vartype' | 'variable';
    commandName?: string;
    parameterIndex?: number;
    partialText?: string;
};

export class CompletionContextDetector {
    /**
     * Get the text from the start of the line up to the cursor position
     */
    public getTextUntilPosition(model: MonacoModel, position: IPosition): string {
        const line = model.getLineContent(position.lineNumber);
        return line.substring(0, position.column - 1);
    }

    /**
     * Check if we're in a variable or array declaration context
     * Returns true if we should show NO completions (user is typing new name)
     */
    public isVariableDeclarationContext(textUntilPosition: string): boolean {
        // Check for var declaration: "var " or "var x" (partial name)
        if (/\bvar\s+[a-z][a-z0-9_]*$/.test(textUntilPosition) || /\bvar\s*$/.test(textUntilPosition)) {
            return true;
        }

        // Check for array declaration: "array " or "array x" (partial name)
        if (/\barray\s+[a-z][a-z0-9_]*$/.test(textUntilPosition) || /\barray\s*$/.test(textUntilPosition)) {
            return true;
        }

        // Check for var with type: "var x as " or "var x as T" (partial type)
        if (/\bvar\s+[a-z][a-z0-9_]*\s+as\s+\w*$/.test(textUntilPosition)) {
            return false; // This is VarType context, not declaration
        }

        return false;
    }

    /**
     * Check if we're in a VarType context (after "var x as ")
     */
    public isVarTypeContext(textUntilPosition: string): boolean {
        // Pattern: "var variableName as " or "var variableName as T" (partial type)
        return /\bvar\s+[a-z][a-z0-9_]*\s+as\s+\w*$/.test(textUntilPosition);
    }

    /**
     * Detect which parameter of a command we're in
     * Returns { commandName, parameterIndex, partialText }
     */
    public detectParameterContext(
        textUntilPosition: string,
        commandName: string,
    ): {
        parameterIndex: number;
        partialText: string;
    } | null {
        // Find the command in the text
        const commandRegex = new RegExp(`\\b${commandName}\\s+(.*)$`);
        const match = textUntilPosition.match(commandRegex);

        if (!match) {
            return null;
        }

        const afterCommand = match[1];

        // Count parameters by splitting on whitespace
        // Filter out empty strings
        const tokens = afterCommand.split(/\s+/).filter((t) => t.length > 0);

        // If there's a trailing space, we're starting a new parameter
        const hasTrailingSpace = afterCommand !== afterCommand.trimEnd();

        if (hasTrailingSpace) {
            // We're at the start of a new parameter
            return {
                parameterIndex: tokens.length,
                partialText: '',
            };
        }
        // We're typing a parameter
        return {
            parameterIndex: Math.max(0, tokens.length - 1),
            partialText: tokens[tokens.length - 1] || '',
        };
    }

    /**
     * Detect if we're after a specific command and get context
     */
    public detectCommandContext(textUntilPosition: string, commandName: string): CompletionContext | null {
        const context = this.detectParameterContext(textUntilPosition, commandName);

        if (!context) {
            return null;
        }

        return {
            type: 'parameter',
            commandName,
            parameterIndex: context.parameterIndex,
            partialText: context.partialText,
        };
    }

    /**
     * Detect optional argument parameters (e.g., eis_acdc(...), add_meas(...), poly_we(...), time(...))
     * These use parentheses instead of spaces
     */
    public detectOptionalArgContext(textUntilPosition: string): CompletionContext | null {
        // Match optional argument patterns: "arg_name(" or "arg_name(text" or "arg_name(1 aa "
        const optionalArgMatch = OPTIONAL_ARG_CONTEXT_REGEX.exec(textUntilPosition);

        if (optionalArgMatch) {
            const argName = optionalArgMatch[1];
            const afterParen = optionalArgMatch[2];

            // Count parameters inside the optional argument by splitting on whitespace
            const tokens = afterParen.split(/\s+/).filter((t) => t.length > 0);
            const hasTrailingSpace = afterParen !== afterParen.trimEnd();

            if (hasTrailingSpace) {
                // We're at the start of a new parameter
                return {
                    type: 'parameter',
                    commandName: argName,
                    parameterIndex: tokens.length,
                    partialText: '',
                };
            }

            // We're typing a parameter
            return {
                type: 'parameter',
                commandName: argName,
                parameterIndex: Math.max(0, tokens.length - 1),
                partialText: tokens[tokens.length - 1] || '',
            };
        }

        return null;
    }

    /**
     * Detect context for pck_add command (expects variable name)
     */
    public detectPckAddContext(textUntilPosition: string): { partialText: string } | null {
        const match = /\bpck_add\s+(\S*)$/.exec(textUntilPosition);
        if (match) {
            return {
                partialText: match[1] || '',
            };
        }
        return null;
    }

    /**
     * Detect context for meas command
     * Returns parameter index (0 = duration, 1 = destination, 2 = vartype)
     */
    public detectMeasContext(textUntilPosition: string): {
        parameterIndex: number;
        partialText: string;
    } | null {
        return this.detectParameterContext(textUntilPosition, 'meas');
    }

    /**
     * Detect context for meas_fast commands (meas_fast_*, meas_ms_*)
     * Returns parameter index and which variant
     */
    public detectMeasFastContext(textUntilPosition: string): {
        variant: string;
        parameterIndex: number;
        partialText: string;
    } | null {
        // Match any meas_fast_* or meas_ms_* command
        const match = /\b(meas_fast_\w+|meas_ms_\w+)\s+(.*)$/.exec(textUntilPosition);

        if (!match) {
            return null;
        }

        const variant = match[1];
        const afterCommand = match[2];

        // Count parameters
        const tokens = afterCommand.split(/\s+/).filter((t) => t.length > 0);
        const hasTrailingSpace = afterCommand !== afterCommand.trimEnd();

        if (hasTrailingSpace) {
            return {
                variant,
                parameterIndex: tokens.length,
                partialText: '',
            };
        }
        return {
            variant,
            parameterIndex: Math.max(0, tokens.length - 1),
            partialText: tokens[tokens.length - 1] || '',
        };
    }

    /**
     * Detect context for meas_loop commands (meas_loop_*)
     * Returns parameter index and which meas_loop variant
     */
    public detectMeasLoopContext(textUntilPosition: string): {
        variant: string;
        parameterIndex: number;
        partialText: string;
    } | null {
        // Match any meas_loop_* command
        const match = /\b(meas_loop_\w+)\s+(.*)$/.exec(textUntilPosition);

        if (!match) {
            return null;
        }

        const variant = match[1];
        const afterCommand = match[2];

        // Count parameters
        const tokens = afterCommand.split(/\s+/).filter((t) => t.length > 0);
        const hasTrailingSpace = afterCommand !== afterCommand.trimEnd();

        if (hasTrailingSpace) {
            return {
                variant,
                parameterIndex: tokens.length,
                partialText: '',
            };
        }
        return {
            variant,
            parameterIndex: Math.max(0, tokens.length - 1),
            partialText: tokens[tokens.length - 1] || '',
        };
    }

    /**
     * Check if we're at the beginning of a line (command context)
     */
    public isCommandStartContext(textUntilPosition: string): boolean {
        const trimmed = textUntilPosition.trim();

        // Empty line or only partial word (no spaces yet)
        if (trimmed === '' || !/\s/.test(trimmed)) {
            return true;
        }

        return false;
    }

    /**
     * Get the partial word being typed
     */
    public getPartialWord(textUntilPosition: string): string {
        const words = textUntilPosition.split(/\s+/);
        return words[words.length - 1] || '';
    }

    /**
     * Main detection method - determines overall context
     */
    public detectContext(model: MonacoModel, position: IPosition): CompletionContext {
        const textUntilPosition = this.getTextUntilPosition(model, position);

        // Check variable/array declaration first (highest priority - show nothing)
        if (this.isVariableDeclarationContext(textUntilPosition)) {
            return { type: 'none' };
        }

        // Check for optional argument parameters (e.g., eis_acdc(...))
        const optionalArgContext = this.detectOptionalArgContext(textUntilPosition);
        if (optionalArgContext) {
            return optionalArgContext;
        }

        // Check VarType context (after "var x as ")
        if (this.isVarTypeContext(textUntilPosition)) {
            return {
                type: 'vartype',
                partialText: this.getPartialWord(textUntilPosition),
            };
        }

        // Check pck_add context (expects variable)
        const pckAddContext = this.detectPckAddContext(textUntilPosition);
        if (pckAddContext) {
            return {
                type: 'variable',
                commandName: 'pck_add',
                partialText: pckAddContext.partialText,
            };
        }

        // Check meas command context
        const measContext = this.detectMeasContext(textUntilPosition);
        if (measContext !== null) {
            return {
                type: 'parameter',
                commandName: 'meas',
                parameterIndex: measContext.parameterIndex,
                partialText: measContext.partialText,
            };
        }

        // Check meas_fast command context
        const measFastContext = this.detectMeasFastContext(textUntilPosition);
        if (measFastContext !== null) {
            return {
                type: 'parameter',
                commandName: measFastContext.variant,
                parameterIndex: measFastContext.parameterIndex,
                partialText: measFastContext.partialText,
            };
        }

        // Check meas_loop command context
        const measLoopContext = this.detectMeasLoopContext(textUntilPosition);
        if (measLoopContext !== null) {
            return {
                type: 'parameter',
                commandName: measLoopContext.variant,
                parameterIndex: measLoopContext.parameterIndex,
                partialText: measLoopContext.partialText,
            };
        }

        // Check for ANY command that has a defined parameter count in COMMAND_METADATA.
        // This covers all commands (simple, vartype-related, etc.) without
        // maintaining separate hardcoded lists.
        const commandMatch = /\b([a-z_][a-z0-9_]*:?)\s+/.exec(textUntilPosition);
        if (commandMatch) {
            const potentialCommand = commandMatch[1];
            if (hasDefinedParameterCount(potentialCommand)) {
                const cmdContext = this.detectCommandContext(textUntilPosition, potentialCommand);
                if (cmdContext) {
                    return cmdContext;
                }
            }
        }

        // Check if we're at command start (beginning of line)
        if (this.isCommandStartContext(textUntilPosition)) {
            return {
                type: 'command',
                partialText: this.getPartialWord(textUntilPosition),
            };
        }

        // Default: general command context
        return {
            type: 'command',
            partialText: this.getPartialWord(textUntilPosition),
        };
    }
}
